import { tagMappingService } from '../../../services/TagMappingService.js';
import { getByPath, getCurrentLanguage } from '../../translations.js';
import { filterScenarioTagOptionsForSfw } from '../../utils/nsfwTags.js';
import {
  normalizeTagIds,
  renderScenarioTagPills,
  syncScenarioTagPillState
} from './scenarioTagPills.js';

const MAX_TAGS = 5;

/** @type {Array<{ id: number, label: string, tooltip: string, slug: string }>} */
let loadedTagOptions = [];
let isInitialized = false;

const getHiddenInput = () => document.getElementById('scenario-tag-search');
const getPicker = () => document.getElementById('scenario-tags-picker');
const getCountEl = () => document.getElementById('scenario-tags-count');
const getToggleBtn = () => document.getElementById('scenario-tags-toggle');

const resolveScenarioText = (key, fallback) => {
  const lang = getCurrentLanguage();
  const t = window.translations?.[lang]?.['create-scenario'] || {};
  const enT = window.translations?.en?.['create-scenario'] || {};
  return getByPath(t, key) || getByPath(enT, key) || fallback;
};

const syncToggleLabel = () => {
  const btn = getToggleBtn();
  const picker = getPicker();
  if (!btn || !picker) return;
  const expanded = !picker.classList.contains('is-collapsed');
  const key = expanded ? 'steps.details.tags_show_less' : 'steps.details.tags_show_more';
  btn.setAttribute('data-translate', key);
  btn.setAttribute('aria-expanded', String(expanded));
  btn.textContent = resolveScenarioText(key, expanded ? 'Show less' : 'Show more');
};

const syncCountUI = (count) => {
  const el = getCountEl();
  if (!el) return;
  el.textContent = `${count} / ${MAX_TAGS}`;
  el.classList.toggle('scenario-tags-count--max', count >= MAX_TAGS);
};

const updateHiddenInput = (tagIds) => {
  const input = getHiddenInput();
  const ids = normalizeTagIds(tagIds);
  if (input) {
    input.value = JSON.stringify(ids);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
  syncCountUI(ids.length);
};

const handleSelectionChange = (nextIds) => {
  updateHiddenInput(nextIds);
  syncPickerState(nextIds);
};

const syncPickerState = (selectedIds) => {
  syncScenarioTagPillState(getPicker(), selectedIds, MAX_TAGS);
  syncCountUI(normalizeTagIds(selectedIds).length);
};

const buildPicker = (tagOptions) => {
  const picker = getPicker();
  if (!picker) return;
  const current = getScenarioTags();
  renderScenarioTagPills(picker, tagOptions, {
    selectedIds: current,
    getSelectedIds: getScenarioTags,
    maxTags: MAX_TAGS,
    onChange: handleSelectionChange
  });
  syncCountUI(current.length);
  initToggle();
  syncToggleLabel();
};

const initToggle = () => {
  const btn = getToggleBtn();
  const picker = getPicker();
  if (!btn || !picker) return;
  if (btn.dataset.toggleBound) return;
  btn.dataset.toggleBound = 'true';
  btn.addEventListener('click', () => {
    picker.classList.toggle('is-collapsed');
    syncToggleLabel();
  });
};

/** @returns {number[]} */
export const getScenarioTags = () => {
  const input = getHiddenInput();
  if (!input || !input.value) return [];
  try {
    const parsed = JSON.parse(input.value);
    return tagMappingService.resolveScenarioTagIds(Array.isArray(parsed) ? parsed : []);
  } catch {
    return [];
  }
};

/** Accepts numeric ids and/or legacy label/slug strings. */
export const setScenarioTags = (tags) => {
  if (!Array.isArray(tags)) return;
  const resolved = tagMappingService.resolveScenarioTagIds(tags);
  const allowedIds = new Set(loadedTagOptions.map((t) => t.id));
  const filtered = resolved.filter((id) => allowedIds.size === 0 || allowedIds.has(id));
  const clamped = filtered.slice(0, MAX_TAGS);
  updateHiddenInput(clamped);
  syncPickerState(clamped);
};

export const clearScenarioTags = () => {
  updateHiddenInput([]);
  syncPickerState([]);
};

export const syncScenarioTagsPickerFromForm = () => {
  if (loadedTagOptions.length) buildPicker(loadedTagOptions);
};

export async function initializeScenarioTags() {
  try {
    loadedTagOptions = filterScenarioTagOptionsForSfw(await tagMappingService.getAllScenarioTagOptions());
    buildPicker(loadedTagOptions);
    isInitialized = true;
  } catch (error) {
    console.warn('[ScenarioTags] Failed to load tags:', error);
    loadedTagOptions = [];
    buildPicker(loadedTagOptions);
    isInitialized = true;
  }
}

document.addEventListener('mappingsUpdated', async () => {
  if (isInitialized) {
    await initializeScenarioTags();
  }
});

document.addEventListener('languageChanged', () => {
  if (isInitialized) syncToggleLabel();
});
